import { useCallback, useEffect, useRef, useState } from "react";
import {
  AdAttribute,
  AutoPlayType,
  PageTypes,
  VideoEvents
} from "../../globals";

import sendVideoAnalytics from "./sendVideoAnalytics";
import { EventLabel, getPageType, getPlayerType, VideoEvent } from "./utils";
import { useVideoDataContext } from "../../orca/contexts/VideoDataContext";
import { useVideoElementRefContext } from "../../orca/contexts/VideoElementRefContext";
import { useAutoplayContext } from "../../orca/contexts/AutoplayContext";
import {
  autoplayStateAllowsAds,
  getShouldShowAds,
  isAdAllowed
} from "../../orca/ads/utils/helpers";

export default function useAnalytics(config) {
  const videoElementRef = useVideoElementRefContext();
  const { videoData, uniqueId } = useVideoDataContext();
  const { autoplayState } = useAutoplayContext();

  const { isCarousel, debug } = config;

  const [avAdMeta, setAvAdMeta] = useState([]);
  const [adAttribute, setAdAttribute] = useState("undefined");
  const [isAnalyticsInitialized, setAnalyticsInitialized] = useState(false);
  const [playerType, setPlayerType] = useState(getPlayerType(config));
  const quartile = useRef(0);

  const videoElement = videoElementRef.current;

  const sendAnalyticsEvent = useCallback(
    (eventAction, data = {}) => {
      sendVideoAnalytics({
        videoData,
        eventAction,
        config,
        videoElementRef,
        avAdMeta,
        adAttribute,
        autoplayState,
        playerType,
        ...data
      });
    },
    [
      videoData,
      config,
      videoElementRef,
      avAdMeta,
      adAttribute,
      autoplayState,
      playerType
    ]
  );

  useEffect(() => {
    if (
      isAnalyticsInitialized ||
      !videoData._id ||
      !autoplayState.isInitialized
    )
      return;

    window.dataLayer = window.dataLayer || [];

    // Send first impression, videos are lazy-loaded in Spectrum, so it should send once scrolled to.
    // impression event should fire once per video player (and not fire again for new videos on playthrough)
    if (!isCarousel) {
      sendAnalyticsEvent(VideoEvent.IMPRESSION, {
        // autoplaying videos will always  start muted
        soundMuted: autoplayState.isAutoplay ? true : undefined
      });
    }

    if (!isAdAllowed(videoData, config)) {
      setAdAttribute(AdAttribute.NOT_ALLOWED);
    } else if (
      !getShouldShowAds(config, videoData?._id) ||
      !autoplayStateAllowsAds(autoplayState)
    ) {
      setAdAttribute(AdAttribute.DISABLED);
    }

    setAnalyticsInitialized(true);
  }, [
    videoData?._id,
    autoplayState,
    isAnalyticsInitialized,
    isCarousel,
    sendAnalyticsEvent
  ]);

  const INTERVAL = 5;
  const progress = useRef(INTERVAL);
  const lastKnownTime = useRef(0);
  const timeSinceLastUpdate = useRef(0);

  const handleEarlyExit = useCallback(() => {
    if (timeSinceLastUpdate.current > 0) {
      sendAnalyticsEvent(VideoEvent.VIDEO_PROGRESS, {
        videostart_id: uniqueId,
        eventLabel: EventLabel.PROGRESS,
        engaged_time: timeSinceLastUpdate.current
      });
      timeSinceLastUpdate.current = 0;
    }
  }, [sendAnalyticsEvent, uniqueId]);

  const handleTimeUpdate = useCallback(
    e => {
      const dif = e.target.currentTime - lastKnownTime.current;
      lastKnownTime.current = e.target.currentTime;

      // This check prevents us from sending update when user jumps to a new point in video
      if (dif > 0 && dif < 1) {
        timeSinceLastUpdate.current += dif;
      }

      if (timeSinceLastUpdate.current > INTERVAL) {
        sendAnalyticsEvent(VideoEvent.VIDEO_PROGRESS, {
          videostart_id: uniqueId,
          eventLabel: EventLabel.PROGRESS,
          engaged_time: INTERVAL
        });
        timeSinceLastUpdate.current = 0;
      }
      if (
        (e.target?.currentTime / e.target.duration) * 100 >=
        progress.current
      ) {
        if (progress.current >= 25 && quartile.current < 25) {
          quartile.current = 25;
          sendAnalyticsEvent(VideoEvent.QUARTILE_25);
        }
        if (progress.current >= 50 && quartile.current < 50) {
          quartile.current = 50;
          sendAnalyticsEvent(VideoEvent.QUARTILE_50);
        }
        if (progress.current >= 75 && quartile.current < 75) {
          quartile.current = 75;
          sendAnalyticsEvent(VideoEvent.QUARTILE_75);
        }
        progress.current = progress.current + INTERVAL;
      }
    },
    [sendAnalyticsEvent, uniqueId, debug]
  );

  useEffect(() => {
    if (!videoElement || (!autoplayState.isInitialized && !isCarousel)) return;

    window.dataLayer = window.dataLayer || [];

    videoElement.addEventListener(VideoEvents.TIME_UPDATE, handleTimeUpdate);
    videoElement.addEventListener(VideoEvents.EARLY_EXIT, handleEarlyExit);
    videoElement.addEventListener(VideoEvents.ENDED, handleEarlyExit);

    return () => {
      videoElement.removeEventListener(
        VideoEvents.TIME_UPDATE,
        handleTimeUpdate
      );
      videoElement.removeEventListener(VideoEvents.EARLY_EXIT, handleEarlyExit);
      videoElement.removeEventListener(VideoEvents.ENDED, handleEarlyExit);
    };
  }, [videoElement, isCarousel, autoplayState]);

  useEffect(() => {
    return () => {
      handleEarlyExit();
    };
  }, []);

  const sendVideoStartEvent = (autoplayState, analyticsData = {}) => {
    // for live preview videos, we send an autoplay event on impression
    if (autoplayState.isLivePreview) {
      sendAnalyticsEvent(VideoEvent.AUTOPLAY);
      return;
    }

    if (!autoplayState.isAutoplay) {
      sendAnalyticsEvent(VideoEvent.START, { ...analyticsData });
      return;
    }

    const AutoplayEventMap = {
      [PageTypes.ARTICLE]: VideoEvent.START,
      [PageTypes.HOME]: VideoEvent.AUTOPLAY
    };

    // We set a different value for adAttribute depending on autoplay type
    const AdAttributeMap = {
      [AutoPlayType.AUTO]: AdAttribute.DISABLED,
      [AutoPlayType.LOOP]: AdAttribute.NOT_SUPPORTED,
      [AutoPlayType.LOOP_NO_CONTROLS]: AdAttribute.NOT_SUPPORTED
    };

    const pageType = getPageType();
    const event = AutoplayEventMap[pageType] || VideoEvent.START;
    const adAttribute =
      analyticsData.adAttribute || AdAttributeMap[autoplayState.type];
    setAdAttribute(adAttribute);

    sendAnalyticsEvent(event, { ...analyticsData, adAttribute });
  };

  return {
    sendAnalyticsEvent,
    avAdMeta,
    setAvAdMeta,
    adAttribute,
    setAdAttribute,
    isAnalyticsInitialized,
    setPlayerType,
    sendVideoStartEvent
  };
}
